import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Shield, Wrench, CheckCircle, X, Eye, EyeOff } from "lucide-react";
import { ImageAnalysis } from "@mangelapp/shared";
import { useTranslation } from 'react-i18next';

interface ImageAnalysisDialogProps {
  isOpen: boolean;
  onClose: () => void;
  analysis: ImageAnalysis | any; // Allow any for API response format
  onApprove: (userDescription: string, useForTicketDescription: boolean, saveAsImageDescription: boolean) => void | Promise<void>;
  onReject: () => void;
  imageUrl?: string; // GCS URL
  imagePath?: string; // Legacy path (for backward compatibility)
  ticketId?: string;
  onUseForTicketDescription?: (description: string) => void;
  onSaveAsImageDescription?: (description: string) => void;
}

export default function ImageAnalysisDialog({
  isOpen,
  onClose,
  analysis,
  onApprove,
  onReject,
  imageUrl,
  imagePath,
  ticketId,
  onUseForTicketDescription,
  onSaveAsImageDescription,
}: ImageAnalysisDialogProps) {
  const [isApproving, setIsApproving] = useState(false);
  const [editedDescription, setEditedDescription] = useState(analysis?.analysisText || '');
  const [useForTicket, setUseForTicket] = useState(false);
  const [saveAsImageDesc, setSaveAsImageDesc] = useState(true); // Default to true - save as image description by default
  const { t } = useTranslation(['components.imageAnalysis', 'components.ticketHistory']);

  // Update edited description when analysis changes
  useEffect(() => {
    if (analysis?.analysisText) {
      setEditedDescription(analysis.analysisText);
    }
  }, [analysis?.analysisText]);

  if (!analysis) return null;

  // Determine image source
  const imageSrc = imageUrl || (imagePath ? `/api/files/${imagePath}` : '');

  const getRiskLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'low':
      case 'niedrig':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'medium':
      case 'mittel':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'high':
      case 'hoch':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'critical':
      case 'kritisch':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRiskIcon = (level: string) => {
    switch (level.toLowerCase()) {
      case 'low':
      case 'niedrig':
        return <CheckCircle className="w-4 h-4" />;
      case 'medium':
      case 'mittel':
        return <Eye className="w-4 h-4" />;
      case 'high':
      case 'hoch':
        return <AlertTriangle className="w-4 h-4" />;
      case 'critical':
      case 'kritisch':
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
      default:
        return <Eye className="w-4 h-4" />;
    }
  };

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      // Call onApprove with all the details
      await onApprove(editedDescription, useForTicket, saveAsImageDesc);
      onClose();
    } catch (error) {
      console.error('Error approving analysis:', error);
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    try {
      await onReject();
      onClose();
    } catch (error) {
      console.error('Fehler beim Ablehnen:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 gap-0 bg-white">
        <DialogHeader className="p-6 pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Eye className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {t('components.imageAnalysis:title')}
              </DialogTitle>
              <p className="text-sm text-gray-500 mt-1">
                {t('components.imageAnalysis:subtitle')}
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-[60vh] px-6">
            {/* Bild-Vorschau */}
            {imageSrc && (
              <div className="mb-6">
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <img 
                    src={imageSrc} 
                    alt={t('components.imageAnalysis:imageAlt') as string}
                    className="w-full max-h-48 object-contain rounded-md"
                  />
                </div>
              </div>
            )}

            {/* Risiko-Übersicht */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center">
                <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium ${getRiskLevelColor(analysis.riskLevel)}`}>
                  {getRiskIcon(analysis.riskLevel)}
                  {t('components.imageAnalysis:overview.totalRisk')}: {analysis.riskLevel}
                </div>
              </div>
              
              <div className="text-center">
                <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium ${getRiskLevelColor(analysis.defectSeverity || 'medium')}`}>
                  <Wrench className="w-4 h-4" />
                  {t('components.imageAnalysis:overview.severity')}: {analysis.defectSeverity || t('components.ticketHistory:unknown')}
                </div>
              </div>

              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium bg-blue-100 text-blue-800 border-blue-200">
                  <CheckCircle className="w-4 h-4" />
                  {t('components.imageAnalysis:overview.confidence')}: {analysis.confidence}%
                </div>
              </div>
            </div>

            <Separator className="mb-6" />

            {/* Erkannte Mängel */}
            {analysis.detectedDefects && analysis.detectedDefects.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                  <h3 className="font-medium text-gray-900">{t('components.imageAnalysis:sections.defects')}</h3>
                  <Badge variant="secondary" className="ml-auto">
                    {analysis.detectedDefects.length}
                  </Badge>
                </div>
                <div className="space-y-2">
                  {analysis.detectedDefects.map((defect, index) => (
                    <div key={index} className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <p className="text-sm text-orange-800">{defect}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sicherheitsrisiken */}
            {analysis.safetyRisks && analysis.safetyRisks.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-5 h-5 text-red-600" />
                  <h3 className="font-medium text-gray-900">{t('components.imageAnalysis:sections.safety')}</h3>
                  <Badge variant="destructive" className="ml-auto">
                    {analysis.safetyRisks.length}
                  </Badge>
                </div>
                <div className="space-y-2">
                  {analysis.safetyRisks.map((risk, index) => (
                    <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-800">{risk}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Restarbeiten */}
            {analysis.remainingWork && analysis.remainingWork.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Wrench className="w-5 h-5 text-blue-600" />
                  <h3 className="font-medium text-gray-900">{t('components.imageAnalysis:sections.remaining')}</h3>
                  <Badge variant="outline" className="ml-auto">
                    {analysis.remainingWork.length}
                  </Badge>
                </div>
                <div className="space-y-2">
                  {analysis.remainingWork.map((work, index) => (
                    <div key={index} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">{work}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Qualitätsprobleme */}
            {analysis.qualityIssues && analysis.qualityIssues.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <EyeOff className="w-5 h-5 text-purple-600" />
                  <h3 className="font-medium text-gray-900">{t('components.imageAnalysis:sections.quality')}</h3>
                  <Badge variant="outline" className="ml-auto bg-purple-50 text-purple-700">
                    {analysis.qualityIssues.length}
                  </Badge>
                </div>
                <div className="space-y-2">
                  {analysis.qualityIssues.map((issue, index) => (
                    <div key={index} className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <p className="text-sm text-purple-800">{issue}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Vollständiger Bericht - Editable */}
            <div className="mb-6">
              <h3 className="font-medium text-gray-900 mb-3">{t('components.imageAnalysis:sections.report')}</h3>
              <Textarea
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                className="min-h-[120px] text-sm"
                placeholder={t('components.imageAnalysis:descriptionPlaceholder') as string || 'Edit the AI-generated description...'}
              />
            </div>

            {/* Options */}
            <div className="mb-6 space-y-3">
              {onUseForTicketDescription && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="use-for-ticket"
                    checked={useForTicket}
                    onCheckedChange={(checked) => setUseForTicket(checked === true)}
                  />
                  <label
                    htmlFor="use-for-ticket"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {t('components.imageAnalysis:options.useForTicket') || 'Use for ticket description'}
                  </label>
                </div>
              )}
              {onSaveAsImageDescription && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="save-as-image-desc"
                    checked={saveAsImageDesc}
                    onCheckedChange={(checked) => setSaveAsImageDesc(checked === true)}
                  />
                  <label
                    htmlFor="save-as-image-desc"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {t('components.imageAnalysis:options.saveAsImageDesc') || 'Save as image description'}
                  </label>
                </div>
              )}
            </div>

            {/* Empfohlene Priorität */}
            {analysis.priorityRecommendation && (
              <div className="mb-6">
                <h3 className="font-medium text-gray-900 mb-3">{t('components.imageAnalysis:sections.priority')}</h3>
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border font-medium ${getRiskLevelColor(analysis.priorityRecommendation)}`}>
                  {getRiskIcon(analysis.priorityRecommendation)}
                  {analysis.priorityRecommendation}
                </div>
              </div>
            )}
          </ScrollArea>
        </div>

        <DialogFooter className="p-6 pt-4 border-t border-gray-200">
          <div className="flex gap-3 w-full">
            <Button
              variant="outline"
              onClick={handleReject}
              className="flex-1"
            >
              <X className="w-4 h-4 mr-2" />
              {t('components.imageAnalysis:actions.reject')}
            </Button>
            <Button
              onClick={handleApprove}
              disabled={isApproving}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {isApproving ? t('components.imageAnalysis:actions.approving') : t('components.imageAnalysis:actions.approve')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
